import { LOCALE_COOKIE } from "./i18n/locale.ts";
import { THEME_COOKIE } from "./theme.ts";

/**
 * Theme and locale live in cookies rather than localStorage: the SSR loader
 * reads them on the very first request, which is what keeps the server-
 * rendered HTML from flashing the wrong theme or language.
 */
export function writePreferenceCookie(name: string, value: string, maxAgeDays = 365): void {
  if (typeof document === "undefined") return;
  // The Cookie Store API the rule points at is Chromium's alone — not Safari's,
  // which is the browser this app is mostly read in, and a preference written
  // through an API half the visitors lack does not survive their next reload.
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store is Chromium-only and Safari is most of this app's traffic
  document.cookie = preferenceCookie(
    name,
    value,
    maxAgeDays,
    window.location.protocol === "https:",
  );
}

/**
 * The cookie line itself. `Secure` where the page came over TLS — the
 * three preferences carry nothing secret, but a cookie that is only ever
 * meant for an https origin should say so — and not on plain http, which
 * is development, where Safari refuses a Secure cookie outright and the
 * picker would stop sticking.
 */
export function preferenceCookie(
  name: string,
  value: string,
  maxAgeDays: number,
  secure: boolean,
): string {
  const maxAge = maxAgeDays * 24 * 60 * 60;
  return `${name}=${value}; Max-Age=${maxAge}; path=/; SameSite=Lax${secure ? "; Secure" : ""}`;
}

/**
 * The two choices written again, for another year, on every boot that
 * finds them. A cookie ages from the day it was written and the picker
 * writes only when pressed: a person who chose Russian and reads the diary
 * every day would have had the page fall back to the browser's language one
 * morning a year on, with no press having changed anything — and the dark
 * scheme the same. The session's own note is renewed the same way, in the
 * store that owns it.
 */
export function renewPreferenceCookies(): void {
  if (typeof document === "undefined") return;
  for (const name of [LOCALE_COOKIE, THEME_COOKIE]) {
    const value = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))?.[1];
    if (value) writePreferenceCookie(name, value);
  }
}
